// Forecast page: pick a hub + horizon and see the daily forecast volume, plus
// which customers drive it. Pure computation from the engine — no new tables.

import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../auth/AuthProvider";
import { Card, Field, Select } from "../../components/ui";
import { getErrorMessage } from "../../lib/errors";
import { addDays, fmtDow, fmtN, today } from "../../lib/format";
import type { Customer, CustomerLane, Hub, HubEvent } from "../../lib/types";
import {
  forecastCustomerHub,
  impliedLoads,
  isCustomerActiveAt,
  reroutedInToHub,
  reroutedOutOfHub,
} from "../../lib/forecast/engine";
import { listCustomers } from "../customers/customersApi";
import { listAccountLanes } from "../customers/lanesApi";
import { listHubs } from "../hubs/hubsApi";
import { listAccountLhSchedule, type LhLaneRow } from "../lhschedule/lhScheduleApi";
import { listHubEvents } from "../hubevents/hubEventsApi";
import { buildOverrideMap, listAccountOverrides, type ForecastOverride } from "./overridesApi";

const HORIZONS = [7, 14, 28];

const dayLabel = (dateStr: string) => fmtDow(dateStr); // "Wed 15/7"

export default function ForecastPage() {
  const { activeAccount } = useAuth();
  const accountId = activeAccount?.accountId ?? null;

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [hubs, setHubs] = useState<Hub[]>([]);
  const [lanes, setLanes] = useState<Array<{ customerId: string; lane: CustomerLane }>>([]);
  const [lhRows, setLhRows] = useState<LhLaneRow[]>([]);
  const [overrides, setOverrides] = useState<ForecastOverride[]>([]);
  const [events, setEvents] = useState<HubEvent[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [hubId, setHubId] = useState("");
  const [horizon, setHorizon] = useState(14);
  const [start] = useState(today());

  useEffect(() => {
    if (!accountId) return;
    Promise.all([
      listCustomers(accountId),
      listHubs(accountId),
      listAccountLanes(accountId),
      listAccountLhSchedule(accountId),
      listAccountOverrides(accountId),
      listHubEvents(accountId),
    ])
      .then(([c, h, ln, lh, ov, ev]) => {
        const activeHubs = h.filter((x) => x.active);
        setCustomers(c);
        setHubs(activeHubs);
        setLanes(ln);
        setLhRows(lh);
        setOverrides(ov);
        setEvents(ev);
        setHubId((prev) => prev || activeHubs[0]?.id || "");
      })
      .catch((e) => setError(getErrorMessage(e, "Failed to load forecast data.")));
  }, [accountId]);

  const days = useMemo(
    () => Array.from({ length: horizon }, (_, i) => addDays(start, i)),
    [start, horizon]
  );

  // Per-lane capacity and weekly planned loads, keyed by "customerId|hubId".
  const capByKey = useMemo(() => {
    const m = new Map<string, number | null | undefined>();
    for (const { customerId, lane } of lanes) m.set(`${customerId}|${lane.hubId}`, lane.loadCapacity);
    return m;
  }, [lanes]);
  const plannedByKey = useMemo(() => {
    const m = new Map<string, number[]>();
    for (const r of lhRows) {
      const key = `${r.customerId}|${r.hubId}`;
      let arr = m.get(key);
      if (!arr) {
        arr = [0, 0, 0, 0, 0, 0, 0];
        m.set(key, arr);
      }
      arr[r.day.dayOfWeek] = r.day.loads;
    }
    return m;
  }, [lhRows]);

  const overrideMap = useMemo(() => buildOverrideMap(overrides), [overrides]);

  const daily = useMemo(() => {
    if (!hubId) return [];
    return days.map((d) => {
      // LH schedule indexes day_of_week 0=Mon..6=Sun; JS getDay() is 0=Sun..6=Sat.
      const lhDow = (new Date(d + "T12:00:00").getDay() + 6) % 7;
      let volume = 0;
      let implied = 0;
      let planned = 0;
      for (const c of customers) {
        const v = forecastCustomerHub(c, hubId, d, overrideMap, events);
        if (v > 0) {
          volume += v;
          implied += impliedLoads(v, capByKey.get(`${c.id}|${hubId}`));
        }
        if (isCustomerActiveAt(c, hubId, d)) {
          planned += plannedByKey.get(`${c.id}|${hubId}`)?.[lhDow] ?? 0;
        }
      }
      const rerouteIn = events.length ? reroutedInToHub(customers, hubId, d, events) : 0;
      const rerouteOut = events.length ? reroutedOutOfHub(customers, hubId, d, events) : 0;
      return { date: d, volume, implied, planned, rerouteIn, rerouteOut };
    });
  }, [customers, hubId, days, capByKey, plannedByKey, overrideMap, events]);
  const grandTotal = useMemo(() => daily.reduce((s, d) => s + d.volume, 0), [daily]);
  const rerouteInTotal = useMemo(() => daily.reduce((s, d) => s + d.rerouteIn, 0), [daily]);
  const rerouteOutTotal = useMemo(() => daily.reduce((s, d) => s + d.rerouteOut, 0), [daily]);
  const anyReroute = rerouteInTotal > 0 || rerouteOutTotal > 0;

  const byCustomer = useMemo(() => {
    if (!hubId) return [];
    return customers
      .map((c) => ({
        name: c.name,
        total: days.reduce((s, d) => s + forecastCustomerHub(c, hubId, d, overrideMap, events), 0),
      }))
      .filter((x) => x.total > 0)
      .sort((a, b) => b.total - a.total);
  }, [customers, hubId, days, overrideMap, events]);

  if (!accountId) {
    return (
      <Card>
        <p className="text-sm text-slate-500">No active account. Try signing out and back in.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <h1 className="text-lg font-semibold tracking-tight text-slate-900">Forecast</h1>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      <Card>
        <div className="grid max-w-xl grid-cols-2 gap-3">
          <Field label="Hub">
            <Select value={hubId} onChange={(e) => setHubId(e.target.value)}>
              {hubs.length === 0 && <option value="">No active hubs</option>}
              {hubs.map((h) => (
                <option key={h.id} value={h.id}>
                  {h.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Horizon">
            <Select value={String(horizon)} onChange={(e) => setHorizon(Number(e.target.value))}>
              {HORIZONS.map((n) => (
                <option key={n} value={n}>
                  Next {n} days
                </option>
              ))}
            </Select>
          </Field>
        </div>
        {hubId && (
          <p className="mt-3 text-sm text-slate-600">
            Total forecast (next {horizon} days):{" "}
            <span className="font-semibold text-slate-900">{fmtN(grandTotal)}</span> packages
            {anyReroute && (
              <span className="text-slate-500">
                {" "}
                · includes{" "}
                <span className="font-medium text-emerald-600">{fmtN(rerouteInTotal)}</span> rerouted
                in
                {rerouteOutTotal > 0 && (
                  <>
                    {" "}
                    ·{" "}
                    <span className="font-medium text-red-600">{fmtN(rerouteOutTotal)}</span> rerouted
                    out
                  </>
                )}
              </span>
            )}
          </p>
        )}
      </Card>

      {hubId && (
        <div className="grid gap-4 lg:grid-cols-2">
          <Card className="p-0">
            <div className="border-b border-slate-200 px-5 py-3 text-sm font-medium text-slate-700">
              Daily forecast
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="px-5 py-2 font-medium">Day</th>
                  <th className="px-5 py-2 text-right font-medium">Forecast (pkg)</th>
                  {anyReroute && (
                    <th
                      className="px-4 py-2 text-right font-medium"
                      title="Net rerouted by hub events: + arriving from other hubs, − sent away"
                    >
                      Reroute
                    </th>
                  )}
                  <th className="px-4 py-2 text-right font-medium" title="Loads needed for the forecast volume">
                    Loads needed
                  </th>
                  <th className="px-5 py-2 text-right font-medium" title="Loads in the LH schedule">
                    Loads planned
                  </th>
                </tr>
              </thead>
              <tbody>
                {daily.map((d) => {
                  const short = d.planned < d.implied; // planned doesn't cover the forecast
                  const net = d.rerouteIn - d.rerouteOut;
                  return (
                    <tr key={d.date} className="border-b border-slate-100 last:border-0">
                      <td className="px-5 py-2 text-slate-700">{dayLabel(d.date)}</td>
                      <td className="px-5 py-2 text-right text-slate-700">{fmtN(d.volume)}</td>
                      {anyReroute && (
                        <td
                          className={`px-4 py-2 text-right ${
                            net > 0 ? "text-emerald-600" : net < 0 ? "text-red-600" : "text-slate-400"
                          }`}
                          title={`${fmtN(d.rerouteIn)} in · ${fmtN(d.rerouteOut)} out`}
                        >
                          {net === 0 ? "—" : `${net > 0 ? "+" : "−"}${fmtN(Math.abs(net))}`}
                        </td>
                      )}
                      <td className="px-4 py-2 text-right text-slate-700">{d.implied}</td>
                      <td
                        className={`px-5 py-2 text-right ${short ? "font-medium text-red-600" : "text-slate-700"}`}
                        title={short ? "Planned loads are below what the forecast needs" : ""}
                      >
                        {d.planned}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Card>

          <Card className="p-0">
            <div className="border-b border-slate-200 px-5 py-3 text-sm font-medium text-slate-700">
              By customer (next {horizon} days)
            </div>
            {byCustomer.length === 0 ? (
              <p className="p-5 text-sm text-slate-500">
                No forecast volume at this hub in the selected window.
              </p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                    <th className="px-5 py-2 font-medium">Customer</th>
                    <th className="px-5 py-2 text-right font-medium">Forecast (pkg)</th>
                  </tr>
                </thead>
                <tbody>
                  {byCustomer.map((c) => (
                    <tr key={c.name} className="border-b border-slate-100 last:border-0">
                      <td className="px-5 py-2 font-medium text-slate-900">{c.name}</td>
                      <td className="px-5 py-2 text-right text-slate-700">{fmtN(c.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}
